import { useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient } from '../../providers/auth-provider';
import { useSdkAlerts, useSdkCatalog, useSdkUsage } from '../../hooks/use-sdk-governance';

import {
  Empty,
  Metric,
  Pill,
  Tabs,
  Td,
  Th,
  fmtWhen,
  inputStyle,
  mono,
  secondaryButton,
  tableStyle,
  theadRowStyle,
} from '../proxy-vault/ui';

/**
 * SDKs — OUR SDKs, cleanly: Versions is the download page (latest, the
 * release-owned minimum supported by this deployment, artifacts) for the two
 * SDKs we ship — client and worker. Support policy is decided at RELEASE
 * time (support-matrix.ts, synced at boot) — deliberately no editors here.
 * Usage is who-runs-what, including partner components (e.g. the AgentOS
 * sa-adapter) as external references; Alerts is the drip ledger.
 */
export function SdkGovernanceView() {
  const [page, setPage] = useState<'versions' | 'usage' | 'alerts'>('versions');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tabs
        tabs={[
          { id: 'versions', label: 'Versions' },
          { id: 'usage', label: 'Usage' },
          { id: 'alerts', label: 'Alerts' },
        ]}
        active={page}
        onChange={(id) => setPage(id as typeof page)}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {page === 'versions' && <VersionsPage />}
        {page === 'usage' && <UsagePage />}
        {page === 'alerts' && <AlertsPage />}
      </div>
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  client: 'client SDK (@shiftagent/client-sdk)',
  worker: 'worker SDK (shiftagent-worker, in each bundle)',
};

function VersionsPage() {
  const api = useApiClient();
  const { catalog, status, error } = useSdkCatalog();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busyDownload, setBusyDownload] = useState<string | null>(null);

  const download = async (kind: string, version: string) => {
    setBusyDownload(`${kind}:${version}`);
    setSaveError(null);
    try {
      const { blob, filename } = await api.downloadSdkArtifact(kind, version);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setSaveError(describeApiError(err, String(err)));
    } finally {
      setBusyDownload(null);
    }
  };

  if (status === 'error') return <Empty>{error ?? 'Failed to load the SDK catalog'}</Empty>;
  if (!catalog) return <Empty>Loading…</Empty>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 940 }}>
      {saveError && <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)' }}>{saveError}</div>}
      <table style={tableStyle} data-testid="sdk-versions-table">
        <thead>
          <tr style={theadRowStyle}>
            <Th first>Kind</Th>
            <Th>Latest</Th>
            <Th>Min supported</Th>
            <Th>Releases</Th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(catalog.kinds).map(([kind, k]) => (
            <tr key={kind}>
              <Td first>{KIND_LABEL[kind] ?? kind}</Td>
              <Td>
                <span style={{ fontFamily: mono }}>{k.latest_version ?? '—'}</span>
              </Td>
              <Td>
                <span style={{ fontFamily: mono }}>{k.min_supported_version ?? '—'}</span>
              </Td>
              <Td>
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                  {k.releases.length === 0 && <span style={{ color: 'var(--text-soft)' }}>—</span>}
                  {k.releases.map((r) => (
                    <span key={r.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <Pill tone={r.status === 'eol' || r.status === 'deprecated' ? 'mute' : undefined}>
                        {r.version} · {r.status}
                      </Pill>
                      {r.downloadable && (
                        <button
                          type="button"
                          data-testid={`sdk-download-${kind}-${r.version}`}
                          style={secondaryButton}
                          disabled={busyDownload === `${kind}:${r.version}`}
                          onClick={() => void download(kind, r.version)}
                        >
                          {busyDownload === `${kind}:${r.version}` ? '…' : '⬇'}
                        </button>
                      )}
                    </span>
                  ))}
                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.6 }}>
        Minimum supported versions are decided at release time and ship with each platform release
        (support matrix in the repo) — they are not editable here.
      </div>
    </div>
  );
}

/**
 * Usage cell text per kind. Partner-owned components are shown as EXTERNAL
 * REFERENCES — context about who is driving our SDKs, never governed rows
 * (e.g. the AgentOS sa-adapter vendors our client SDK; AgentOS sandbox
 * bundles carry our worker SDK).
 */
function usageKindCell(kind: string): string {
  if (kind === 'adapter') return 'external · sa-adapter (AgentOS)';
  if (kind === 'worker') return 'worker — in AgentOS sandbox bundles';
  return kind;
}

function UsagePage() {
  const [kind, setKind] = useState<string>('');
  const { rows, stats, status, error } = useSdkUsage(kind || undefined);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {Object.entries(stats).map(([k, s]) => (
          <Metric key={k} value={`${s.versions}v · ${s.tenants}t`} label={`${k} (${s.total} seen)`} />
        ))}
      </div>
      <div>
        <select style={{ ...inputStyle, width: 260 }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All kinds</option>
          <option value="client">client</option>
          <option value="worker">worker</option>
          <option value="adapter">external references (adapter)</option>
        </select>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.6 }}>
        External components self-report versions here for reference — e.g. the AgentOS sa-adapter that
        uses our client SDK. Only our own SDKs appear under Versions.
      </div>
      {status === 'error' && <Empty>{error ?? 'Failed to load usage'}</Empty>}
      {status === 'success' && rows.length === 0 && (
        <Empty>No observations yet — they appear as requests and runs report their versions.</Empty>
      )}
      {rows.length > 0 && (
        <table style={tableStyle} data-testid="sdk-usage-table">
          <thead>
            <tr style={theadRowStyle}>
              <Th first>Tenant</Th>
              <Th>Kind</Th>
              <Th>Version</Th>
              <Th>Source</Th>
              <Th>First seen</Th>
              <Th>Last seen</Th>
              <Th>Seen</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <Td first>
                  <span style={{ fontFamily: mono, fontSize: 12 }}>{r.tenant_id}</span>
                </Td>
                <Td>{usageKindCell(r.kind)}</Td>
                <Td>
                  <span style={{ fontFamily: mono }}>{r.version}</span>
                </Td>
                <Td>{r.source}</Td>
                <Td>{fmtWhen(r.first_seen_at)}</Td>
                <Td>{fmtWhen(r.last_seen_at)}</Td>
                <Td>{r.seen_count}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AlertsPage() {
  const { alerts, status, error } = useSdkAlerts();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {status === 'error' && <Empty>{error ?? 'Failed to load alerts'}</Empty>}
      {status === 'success' && alerts.length === 0 && (
        <Empty>
          Nothing sent yet. The drip sends one warning per stale version, repeats once a week later,
          and escalates to critical at end-of-life — never per request.
        </Empty>
      )}
      {alerts.length > 0 && (
        <table style={tableStyle} data-testid="sdk-alerts-table">
          <thead>
            <tr style={theadRowStyle}>
              <Th first>When</Th>
              <Th>Tenant</Th>
              <Th>Kind / version</Th>
              <Th>Severity</Th>
              <Th>Sent to</Th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr key={a.id}>
                <Td first>{fmtWhen(a.sent_at)}</Td>
                <Td>
                  <span style={{ fontFamily: mono, fontSize: 12 }}>{a.tenant_id}</span>
                </Td>
                <Td>
                  <span style={{ fontFamily: mono }}>
                    {a.kind}/{a.version}
                  </span>
                </Td>
                <Td>
                  <Pill tone={a.severity === 'critical' ? 'warn' : undefined}>{a.severity}</Pill>
                </Td>
                <Td>{a.sent_to}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
